/**
 * Configuration de l'ÉDITEUR ÉTUDIANT (CodeMirror 6) — source unique.
 *
 * Règles pédagogiques non négociables (AGENTS.md, piège n°6) :
 * - aucune autocomplétion, pas de fermeture automatique des parenthèses/accolades ;
 * - collage et glisser-déposer BLOQUÉS (message d'explication) : les étudiants tapent tout ;
 * - indentation 4 espaces (tabulation réelle pour GDScript, comme l'éditeur Godot).
 *
 * Tout nouvel éditeur étudiant importe d'ici. Le simulateur (`components/simulator/`) en prend
 * tout ; la page d'exercice n'en prend que `getLanguageExtension` et `STUDENT_EDITOR_BASIC_SETUP`,
 * et garde encore ses propres copies du thème, du surlignage des lignes signalées et du
 * blocage du collage. Le retour à la ligne et la hauteur ajustée au script (`wrappedLineIndent`,
 * `scriptHeightTheme`) n'existent que dans la page d'exercice : l'éditeur du simulateur garde une
 * hauteur fixe et ne coupe pas les lignes. Les unifier, c'est les déplacer ici.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cpp } from '@codemirror/lang-cpp';
import { javascript } from '@codemirror/lang-javascript';
import { python } from '@codemirror/lang-python';
import { indentUnit } from '@codemirror/language';
import { EditorView, Decoration, DecorationSet } from '@codemirror/view';
import { Extension, StateField, StateEffect, RangeSetBuilder } from '@codemirror/state';
import { gdscript } from '../languages/gdscript';

/** Thème sombre « C# friendly » appliqué par-dessus oneDark. */
export const csharpDarkTheme = EditorView.theme({
  '&': {
    color: '#d4d4d4',
    backgroundColor: '#1e1e1e'
  },
  '.cm-content': {
    padding: '16px',
    fontFamily: '"Fira Code", "JetBrains Mono", "Consolas", monospace',
    fontSize: '14px',
    lineHeight: '1.5'
  },
  '.cm-focused': {
    outline: 'none'
  },
  '.cm-editor': {
    borderRadius: '8px'
  },
  '.cm-scroller': {
    fontFamily: 'inherit'
  },
  '.cm-gutters': {
    backgroundColor: '#252526',
    color: '#858585',
    border: 'none',
    borderTopLeftRadius: '8px',
    borderBottomLeftRadius: '8px'
  },
  '.cm-activeLineGutter': {
    backgroundColor: '#2a2d2e'
  },
  '.cm-activeLine': {
    backgroundColor: '#2a2d2e'
  },
  '.cm-selectionBackground': {
    backgroundColor: '#264f78'
  },
  '.cm-cursor': {
    borderLeftColor: '#ffffff'
  },
  '.cm-searchMatch': {
    backgroundColor: '#515c6a'
  },
  '.cm-searchMatch.cm-searchMatch-selected': {
    backgroundColor: '#6f8cc4'
  }
}, { dark: true });

/** Extension de langage + unité d'indentation selon le langage de programmation. */
export const getLanguageExtension = (language: string): Extension[] => {
  const lang = language.toLowerCase();
  if (lang === 'python') return [python(), indentUnit.of('    ')];
  if (lang === 'javascript' || lang === 'js') return [javascript(), indentUnit.of('    ')];
  // GDScript : la touche Tab doit insérer une vraie tabulation, comme le template
  // de départ et l'éditeur Godot — sinon on mélange tabs et espaces sans le voir
  if (lang === 'gdscript' || lang === 'godot') return [gdscript(), indentUnit.of('\t')];
  return [cpp(), indentUnit.of('    ')]; // C++ is closest to C# for syntax highlighting
};

/**
 * `basicSetup` de l'éditeur étudiant : aucune aide à la saisie.
 *
 * Pas de `tabSize` ici. Dans `@uiw/react-codemirror`, cette option ne règle pas la largeur des
 * tabulations : elle ajoute `indentUnit.of(' '.repeat(tabSize))`. Comme `basicSetup` passe avant
 * les extensions de la page et que CodeMirror garde la première valeur d'`indentUnit`, elle
 * écrasait la tabulation de GDScript : Tab et Entrée inséraient des espaces sous un code de
 * départ indenté par tabulations. L'unité d'indentation vient de `getLanguageExtension` ; la
 * largeur d'affichage d'une tabulation reste celle de CodeMirror, 4 colonnes.
 */
export const STUDENT_EDITOR_BASIC_SETUP = {
  lineNumbers: true,
  foldGutter: true,
  dropCursor: false,
  allowMultipleSelections: false,
  indentOnInput: true,
  bracketMatching: true,
  // Pédagogie : aucune aide à la saisie, les étudiants tapent tout à la main
  closeBrackets: false,
  autocompletion: false,
  highlightSelectionMatches: false,
  searchKeymap: true,
} as const;

export const PASTE_BLOCKED_MESSAGE = '✍️ Le collage est désactivé — écrire le code soi-même, c\'est comme ça qu\'on le retient !';

/**
 * Collage / drop bloqués + état « message affiché 3,5 s ».
 * Renvoie l'extension CodeMirror à ajouter et le booléen pour afficher le toast.
 */
export function useBlockPasteExtension(): { blockPasteExtension: Extension; pasteBlocked: boolean } {
  const [pasteBlocked, setPasteBlocked] = useState(false);
  const pasteTimerRef = useRef<number | null>(null);

  const notifyPasteBlocked = useCallback(() => {
    setPasteBlocked(true);
    if (pasteTimerRef.current) window.clearTimeout(pasteTimerRef.current);
    pasteTimerRef.current = window.setTimeout(() => setPasteBlocked(false), 3500);
  }, []);

  useEffect(() => () => {
    if (pasteTimerRef.current) window.clearTimeout(pasteTimerRef.current);
  }, []);

  const blockPasteExtension = useMemo(() => EditorView.domEventHandlers({
    paste: (event) => {
      event.preventDefault();
      notifyPasteBlocked();
      return true;
    },
    drop: (event) => {
      event.preventDefault();
      notifyPasteBlocked();
      return true;
    },
  }), [notifyPasteBlocked]);

  return { blockPasteExtension, pasteBlocked };
}

// --- Console d'erreurs (A11) : surlignage des lignes signalées ---

export interface CompilationIssue {
  severity: 'error' | 'warning';
  line: number | null;
  message: string;
}

export type IssueLine = { line: number; severity: 'error' | 'warning' };

// Best-effort : extrait un préfixe « Ligne N: » / « Line N: » du message d'évaluation.
// Pas de ligne parsable → pas de surlignage, jamais d'erreur.
export const parseIssueLine = (message: string): number | null => {
  const match = message.match(/^\s*(?:ligne|line)\s+(\d{1,5})\s*[:.)\-]?/i);
  if (!match) return null;
  const line = parseInt(match[1], 10);
  return Number.isFinite(line) && line > 0 ? line : null;
};

export const setIssueLines = StateEffect.define<IssueLine[]>();

const errorLineDecoration = Decoration.line({ class: 'cm-issueLine-error' });
const warningLineDecoration = Decoration.line({ class: 'cm-issueLine-warning' });

// Surlignage des lignes signalées par l'évaluation ; retiré à la première édition
// du code (pas de mapping des positions : le surlignage n'est plus fiable après édition)
export const issueLinesField = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update(decorations, tr) {
    if (tr.docChanged) return Decoration.none;
    for (const effect of tr.effects) {
      if (effect.is(setIssueLines)) {
        const bySeverity = new Map<number, 'error' | 'warning'>();
        for (const { line, severity } of effect.value) {
          if (line < 1 || line > tr.state.doc.lines) continue; // lignes hors document ignorées
          if (severity === 'error' || !bySeverity.has(line)) bySeverity.set(line, severity);
        }
        const builder = new RangeSetBuilder<Decoration>();
        [...bySeverity.entries()]
          .sort(([lineA], [lineB]) => lineA - lineB)
          .forEach(([line, severity]) => {
            const { from } = tr.state.doc.line(line);
            builder.add(from, from, severity === 'error' ? errorLineDecoration : warningLineDecoration);
          });
        return builder.finish();
      }
    }
    return decorations;
  },
  provide: (field) => EditorView.decorations.from(field),
});

export const issueLinesTheme = EditorView.baseTheme({
  '.cm-issueLine-error': {
    backgroundColor: 'rgba(239, 68, 68, 0.14)',
    boxShadow: 'inset 3px 0 0 rgba(239, 68, 68, 0.6)'
  },
  '.cm-issueLine-warning': {
    backgroundColor: 'rgba(245, 158, 11, 0.10)',
    boxShadow: 'inset 3px 0 0 rgba(245, 158, 11, 0.5)'
  }
});

/** Place le curseur sur une ligne et la fait défiler au centre (clic sur une ligne de la console). */
export function scrollEditorToLine(view: EditorView, line: number): void {
  if (line < 1 || line > view.state.doc.lines) return;
  const { from } = view.state.doc.line(line);
  view.dispatch({ selection: { anchor: from }, effects: EditorView.scrollIntoView(from, { y: 'center' }) });
  view.focus();
}
